import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AfslTemplate, EmailTemplate, PracticeTemplate, SavedParameterWithAfsl, SchedulerState } from './types';
import { fetchAfsls } from 'src/features/reporting/store/thunks';
import { AfslItem, AfslViewModel, ScheduledEvent } from 'src/features/reporting/store/types';
import { ClientItem, fetchPagedResults } from 'src/features/clients/list/store/types';
import { fetchClientList } from 'src/features/clients/list/store/thunks';
import { fetchAfslTemplates, fetchPracticeTemplates, fetchScheduledEventById } from './thunks';
import { fetchReportParametersList } from 'src/features/clients/client/reportsV2/store/thunks';
import { SavedParameter } from 'src/features/clients/client/reportsV2/store/types';
import { fetchAccountSearchResults } from 'src/features/bulk/email/search/store/thunks';
import { AccountSearchResult } from 'src/features/bulk/email/search/store/types';
import { PracticeItem } from './../../../holdings/store/types';

const initialState: SchedulerState = {
  afsls: [],
  clients: [],
  practices: [],
  advisers: [],
  groups: [],
  savedParametersList: [],
  savedParameter: null,
  editingEvent: undefined,
  accountSearchResult: [],
  selectedTemplate: undefined,
  allowStaticClients: false,
  afslTemplates: [],
  practiceTemplates: [],
};

export const schedulerSlice = createSlice({
  name: '@@scheduler',
  initialState,
  reducers: {
    resetEditingSchedulerEvent: (state) => {
      state.editingEvent = undefined;
      state.selectedTemplate = undefined;
    },
    setSavedParameter: (state, action: PayloadAction<SavedParameterWithAfsl | null>) => {
      state.savedParameter = action.payload;
    },
    setAllowStaticClients: (state, action: PayloadAction<boolean>) => {
      state.allowStaticClients = action.payload;
    },
    updatePractices: (state, action: PayloadAction<AfslItem[]>) => {
      state.practices = action.payload.flatMap((x) => x.practices || []);
    },
    updateGroups: (state, action: PayloadAction<AfslItem[]>) => {
      state.groups = action.payload.flatMap((x) => x.groups || []);
    },
    updateAdvisers: (state, action: PayloadAction<PracticeItem[]>) => {
      state.advisers = action.payload.flatMap((x) => x.advisors || []);
    },
    setSelectedTemplate: (state, action: PayloadAction<EmailTemplate | undefined>) => {
      state.selectedTemplate = action.payload;
    },
    resetEmailTemplates: (state) => {
      state.afslTemplates = [];
      state.practiceTemplates = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAfsls.fulfilled, (state, action: PayloadAction<AfslViewModel>) => {
      state.afsls = action.payload.afsls;
      if (state.afsls && state.afsls?.length > 0) {
        state.groups = action.payload.afsls.flatMap((x) => x.groups || []);
      }
      state.practices = [];
      state.advisers = [];
    });
    builder.addCase(fetchClientList.fulfilled, (state, action: PayloadAction<fetchPagedResults<ClientItem>>) => {
      const existingClients = state.clients;
      const newClients = action.payload.results.results;
      const uniqueClientsMap = new Map<number, ClientItem>();

      [...existingClients, ...newClients].forEach((client) => {
        uniqueClientsMap.set(client.clientId, client);
      });
      state.clients = Array.from(uniqueClientsMap.values());
    });
    builder.addCase(fetchScheduledEventById.pending, (state) => {
      state.editingEvent = null;
    });
    builder.addCase(fetchScheduledEventById.fulfilled, (state, action: PayloadAction<ScheduledEvent>) => {
      state.editingEvent = action.payload;
    });
    builder.addCase(fetchAfslTemplates.pending, (state) => {
      state.afslTemplates = [];
    });
    builder.addCase(fetchAfslTemplates.fulfilled, (state, action: PayloadAction<AfslTemplate[]>) => {
      state.afslTemplates = action.payload;
    });
    builder.addCase(fetchPracticeTemplates.pending, (state) => {
      state.practiceTemplates = [];
    });
    builder.addCase(fetchPracticeTemplates.fulfilled, (state, action: PayloadAction<PracticeTemplate[]>) => {
      state.practiceTemplates = action.payload;
    });
    builder.addCase(fetchReportParametersList.fulfilled, (state, action: PayloadAction<SavedParameter[]>) => {
      state.savedParametersList = action.payload.filter(
        (param: SavedParameter, index: number, self: SavedParameter[]) =>
          index === self.findIndex((p) => p.id === param.id)
      );
    });
    builder.addCase(fetchAccountSearchResults.fulfilled, (state, action: PayloadAction<AccountSearchResult[]>) => {
      state.accountSearchResult = action.payload;
    });
  },
});
